/**
 * Given 2 objects, old and new state,
 * it returns a new object where all the keys that are same between the old and the new object are not present and only the keys with different value are present.
 * The values are taken from the new state object.
 * The comparison is done only on first level (shallow compare)
 * @param oldO object with keys and values representing the old state
 * @param newO object with keys and values representing the new state
 * @returns
 */
export const diffStateObjects = (oldO: Record<string, any>, newO: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {}
  for (const key in oldO) {
    const oldValue = oldO[key]
    const newValue = newO[key]

    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      if (oldValue.length !== newValue.length || oldValue.some((v, i) => v !== newValue[i])) {
        result[key] = newValue
      }
    } else if (oldValue != newValue) {
      result[key] = newValue
    }
  }

  for (const key in newO) {
    const oldValue = oldO[key]
    const newValue = newO[key]

    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      if (newValue.length !== oldValue.length || newValue.some((v, i) => v !== oldValue[i])) {
        result[key] = newValue
      }
    } else if (newValue != oldValue) {
      result[key] = newValue
    }
  }
  return result
}

/**
 * Recursively normalizes an object/array for consistent comparison by:
 * 1. Sorting all object keys alphabetically.
 * 2. Deeply normalizing nested structures.
 * 3. Leaving primitives unchanged.
 *
 * @template T - Input type (automatically inferred).
 * @param {T} obj - Object/array/primitive to normalize.
 * @returns {T} - Normalized version with stable key order.
 */
export const normalizeObject = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(normalizeObject) as unknown as T
  }

  if (obj && typeof obj === 'object') {
    const sortedObj: Record<string, unknown> = {}

    Object.keys(obj)
      .sort()
      .forEach((key) => {
        sortedObj[key] = normalizeObject((obj as Record<string, unknown>)[key])
      })
    return sortedObj as T
  }

  return obj
}
